import React, { useState, Fragment, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const ImageGallery = (props) => {
    const { carDetails } = props;
    const [activeImage, setActiveImage] = useState(carDetails.meta_box._al_listing_image_gallery[0].full_url || '');
    const allThumbnails = carDetails.meta_box._al_listing_image_gallery;
    const [visibleThumbnails, setVisibleThumbnails] = useState(allThumbnails.slice(0, 6));
    let [isOpen, setIsOpen] = useState(false);
    
    // Touch handling state
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    
    // Navigation functions for lightbox
    const goToNextImage = useCallback(() => {
        const currentIndex = allThumbnails.findIndex(img => img.full_url === activeImage);
        const nextIndex = currentIndex === allThumbnails.length - 1 ? 0 : currentIndex + 1;
        setActiveImage(allThumbnails[nextIndex].full_url);
    }, [activeImage, allThumbnails]);
    
    const goToPrevImage = useCallback(() => {
        const currentIndex = allThumbnails.findIndex(img => img.full_url === activeImage);
        const prevIndex = currentIndex === 0 ? allThumbnails.length - 1 : currentIndex - 1;
        setActiveImage(allThumbnails[prevIndex].full_url);
    }, [activeImage, allThumbnails]);
    
    // Scroll thumbnail functions
    const scrollThumbnailsLeft = useCallback(() => {
        const firstVisibleThumbnailIndex = allThumbnails.indexOf(visibleThumbnails[0]);
        const newFirstVisibleThumbnailIndex = Math.max(0, firstVisibleThumbnailIndex - 1);
        setVisibleThumbnails(allThumbnails.slice(newFirstVisibleThumbnailIndex, newFirstVisibleThumbnailIndex + 6));
    }, [visibleThumbnails, allThumbnails]);
    
    const scrollThumbnailsRight = useCallback(() => {
        const lastVisibleThumbnailIndex = allThumbnails.indexOf(visibleThumbnails[visibleThumbnails.length - 1]);
        const newFirstVisibleThumbnailIndex = Math.min(allThumbnails.length - 6, lastVisibleThumbnailIndex + 1);
        setVisibleThumbnails(allThumbnails.slice(newFirstVisibleThumbnailIndex, newFirstVisibleThumbnailIndex + 6));
    }, [visibleThumbnails, allThumbnails]);

    // Touch handlers
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };
    
    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };
    
    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const minSwipeDistance = 50;
        
        if (distance > minSwipeDistance) {
            // Left swipe
            goToNextImage();
        } else if (distance < -minSwipeDistance) {
            // Right swipe
            goToPrevImage();
        }
        
        // Reset touch positions
        setTouchStart(0);
        setTouchEnd(0);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isOpen) {
                switch (e.key) {
                    case 'ArrowLeft':
                        goToPrevImage();
                        break;
                    case 'ArrowRight':
                        goToNextImage();
                        break;
                    case 'Escape':
                        setIsOpen(false);
                        break;
                    default:
                        break;
                }
            } else {
                switch (e.key) {
                    case 'ArrowLeft':
                        scrollThumbnailsLeft();
                        break;
                    case 'ArrowRight':
                        scrollThumbnailsRight();
                        break;
                    default:
                        break;
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        
        return () => {
            window.addEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, activeImage, visibleThumbnails, goToNextImage, goToPrevImage, scrollThumbnailsLeft, scrollThumbnailsRight]);

    return (
        <div className="lg:col-span-8 flex flex-col">
            {/* Main image */}
            <div 
                className="aspect-square rounded-lg overflow-hidden bg-gray-100"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <button className='w-full h-full hover:cursor-zoom-in'
                    onClick={() => setIsOpen(true)}>
                    <img
                        src={activeImage}
                        alt={carDetails.title.rendered}
                        className="w-full h-full object-cover object-center"
                    />
                </button>
            </div>
              
            {/* Thumbnail gallery */}
            <div className='relative'>
                {/* previous button scroll left */}
                {allThumbnails.length > 6 && (
                <button
                    onClick={scrollThumbnailsLeft}
                    className="mt-4 absolute left-1 top-12 bg-slate-800 text-white p-2 rounded-full opacity-70 hover:opacity-95 z-10"
                >
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 inline-block"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                    />
                    </svg>
                </button>
                )}
                
                {allThumbnails.length > 0 && (
                <div className="mt-4 grid grid-cols-6 gap-2">
                    {visibleThumbnails.map((image, idx) => (
                    <button
                        key={idx}
                        onClick={() => setActiveImage(image.full_url)}
                        className={`relative aspect-w-1 aspect-h-1 rounded-md overflow-hidden ${
                        activeImage === image.full_url ? 'ring-2 ring-offset-2 ring-red-500' : ''
                        }`}
                    >
                        <img
                        src={image.url}
                        alt={image.alt || `${carDetails.meta_box._al_listing_model_year} ${carDetails.meta_box._al_listing_make_display} ${carDetails.meta_box._al_listing_model_name} - Image ${idx + 1}`}
                        className="w-full h-full object-cover"
                        />
                    </button>
                    ))}
                </div>
                )}
                {/* next button scroll right */}
                {allThumbnails.length > 6 && (
                <button
                    onClick={scrollThumbnailsRight}
                    className="mt-4 absolute right-1 top-12 bg-slate-800 text-white p-2 rounded-full opacity-70 hover:opacity-95 z-2"
                >
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 inline-block"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                    />
                    </svg>
                </button>
                )}
            </div>

            {/* Enhanced Dialog Light Box */}
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-40" onClose={() => setIsOpen(false)}>
                    {/* Darkened background overlay */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-90" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg shadow-xl transition-all">
                                    {/* Close button */}
                                    <button 
                                        onClick={() => setIsOpen(false)}
                                        className="absolute top-4 right-4 bg-white bg-opacity-75 rounded-full p-2 hover:bg-opacity-100 z-10 transition-opacity"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                    
                                    {/* Previous arrow button */}
                                    <button
                                        onClick={goToPrevImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 p-3 rounded-full hover:bg-opacity-100 transition-opacity"
                                        aria-label="Previous image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    
                                    {/* Image container with max viewport constraints */}
                                    <div 
                                        className="max-h-[85vh] max-w-[90vw] flex items-center justify-center"
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                    >
                                        <img 
                                            src={activeImage} 
                                            alt={carDetails.title.rendered} 
                                            className="object-contain max-h-[85vh] max-w-[90vw]" 
                                        />
                                    </div>
                                    
                                    {/* Next arrow button */}
                                    <button
                                        onClick={goToNextImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 p-3 rounded-full hover:bg-opacity-100 transition-opacity"
                                        aria-label="Next image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    
                                    {/* Keyboard navigation hint */}
                                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                                        {allThumbnails.indexOf(allThumbnails.find(img => img.full_url === activeImage)) + 1} / {allThumbnails.length}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default ImageGallery;